import { useEffect, useState } from 'react'

const SCROLL_END_TOLERANCE_PX = 16

/**
 * Sizes a same-origin iframe to its content height, so it has no scroll of its
 * own, and detects when the page is scrolled until the iframe's end is visible.
 *
 * Only page scroll events count, and only after the iframe document loaded: the
 * manual loads its content asynchronously, so a short "loading" document must
 * not be taken as fully read. Once reached, `reachedEnd` stays `true` even if
 * the user scrolls back up. A document that can't be accessed keeps the default
 * height and is never considered read.
 */
export const useFullHeightIframe = () => {
  const [iframe, setIframe] = useState<HTMLIFrameElement | null>(null)
  const [height, setHeight] = useState<number>()
  const [reachedEnd, setReachedEnd] = useState(false)

  useEffect(() => {
    if (!iframe) return

    let observer: ResizeObserver | null = null

    const disconnect = () => {
      observer?.disconnect()
      observer = null
    }

    // The iframe's document is replaced when it navigates, so observe again on every load.
    const observe = () => {
      disconnect()
      try {
        const frameDocument = iframe.contentDocument
        const frameWindow = frameDocument?.defaultView
        if (!frameDocument?.body || !frameWindow) return

        const { body } = frameDocument
        frameDocument.documentElement.style.overflow = 'hidden'
        const measure = () =>
          setHeight(Math.ceil(body.getBoundingClientRect().height))

        measure()
        observer = new frameWindow.ResizeObserver(measure)
        observer.observe(body)
      } catch {
        // Cross-origin document: it can't be measured, so it keeps the default height.
      }
    }

    iframe.addEventListener('load', observe)

    return () => {
      iframe.removeEventListener('load', observe)
      disconnect()
    }
  }, [iframe])

  const measured = height !== undefined

  useEffect(() => {
    if (!iframe || !measured || reachedEnd) return

    const handleScroll = () => {
      const { bottom } = iframe.getBoundingClientRect()
      if (bottom <= window.innerHeight + SCROLL_END_TOLERANCE_PX) {
        setReachedEnd(true)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [iframe, measured, reachedEnd])

  return { iframeRef: setIframe, height, reachedEnd }
}
